import * as React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { SalesSchedulesDto } from '@lib/dto/sales-setup.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { sampleSchedule, scheduleErrors, type ScheduleEntry } from '@lib/helpers/sales-schedule'
import { putSalesSchedulesInputSchema } from '@lib/input/sales-setup.input'
import { calendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSalesSchedules, useSaveSalesSchedules } from '@/hooks/useSales'
import { ScheduleEntryRows } from './ScheduleEntryRows'
import { blankEntry, entriesOf, entryDraft, entryOf, newKey, type EntryDraft } from './scheduleDraft'

interface ScheduleDraft {
  key: string
  id: string | undefined
  name: string
  entries: EntryDraft[]
}

/** What the schedule comes to against $10,000, for an event a year out signed today. */
function Sample({ entries }: { entries: readonly EntryDraft[] }) {
  const parsed = entries.map(entryOf)
  const valid = parsed.filter((e): e is ScheduleEntry => typeof e !== 'string')
  if (valid.length !== parsed.length || scheduleErrors(valid, true).length > 0) return null
  const today = calendarDay(new Date().toISOString().slice(0, 10))
  const resolved = sampleSchedule(valid, today)
  if ('error' in resolved) return <FormError size="sm">On a $10,000 booking these add up to more than the total.</FormError>
  return (
    <p className="m-0 text-xs text-ink-muted">
      On $10,000: {resolved.map((p) => `${p.label} ${dollarsFromCents(p.amountCents)}`).join(' · ')}
    </p>
  )
}

function ScheduleCard({ schedule, onChange, onRemove }: { schedule: ScheduleDraft; onChange: (s: ScheduleDraft) => void; onRemove: () => void }) {
  return (
    <Card asChild className="space-y-3 p-4">
      <li>
        <div className="flex items-center gap-3">
          <Input aria-label="Schedule name" className="flex-1" value={schedule.name} onChange={(e) => onChange({ ...schedule, name: e.target.value })} />
          <Button variant="ghost" size="sm" aria-label={`Remove ${schedule.name}`} onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <ScheduleEntryRows entries={schedule.entries} onChange={(entries) => onChange({ ...schedule, entries })} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange({ ...schedule, entries: [...schedule.entries, blankEntry(`Payment ${String(schedule.entries.length + 1)}`, 'fixed')] })}
          >
            <Plus className="h-3.5 w-3.5" />
            Add a payment
          </Button>
          <Sample entries={schedule.entries} />
        </div>
      </li>
    </Card>
  )
}

function Editor({ data, venueId }: { data: SalesSchedulesDto; venueId: string }) {
  const { toast } = useToast()
  const save = useSaveSalesSchedules(venueId)
  const [drafts, setDrafts] = React.useState<ScheduleDraft[]>(
    data.schedules.map((s) => ({ key: s.id, id: s.id, name: s.name, entries: s.entries.map(entryDraft) })),
  )
  const [error, setError] = React.useState<string | null>(null)

  function handleSave(): void {
    const schedules = []
    for (const d of drafts) {
      const entries = entriesOf(d.entries)
      if (typeof entries === 'string') {
        setError(entries)
        return
      }
      schedules.push({ ...(d.id === undefined ? {} : { id: d.id }), name: d.name, entries })
    }
    const parsed = putSalesSchedulesInputSchema.safeParse({ schedules })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Something here isn’t complete.')
      return
    }
    setError(null)
    save.mutate(parsed.data, {
      onSuccess: () => toast('Payment schedules saved.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <fieldset disabled={!data.canEdit} className="m-0 min-w-0 space-y-4 border-0 p-0">
      <p className="m-0 text-sm text-ink-muted">
        A packet starts from one of these, and can be changed for that couple. The first payment is the deposit; “the rest” makes the schedule add up
        to whatever the couple chooses.
      </p>
      <ul className="m-0 list-none space-y-3 p-0">
        {drafts.map((s) => (
          <ScheduleCard
            key={s.key}
            schedule={s}
            onChange={(next) => setDrafts((all) => all.map((d) => (d.key === s.key ? next : d)))}
            onRemove={() => setDrafts((all) => all.filter((d) => d.key !== s.key))}
          />
        ))}
      </ul>
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          setDrafts((all) => [
            ...all,
            { key: newKey(), id: undefined, name: 'Standard', entries: [{ ...blankEntry('Deposit', 'percent'), amount: '30' }, blankEntry('Final payment', 'rest')] },
          ])
        }
      >
        <Plus className="h-3.5 w-3.5" />
        Add a schedule
      </Button>
      {error === null ? null : <FormError>{error}</FormError>}
      {data.canEdit ? (
        <FormActions>
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save schedules'}
          </Button>
        </FormActions>
      ) : null}
    </fieldset>
  )
}

/** A venue's saved payment schedules (docs/sales.spec.md, "Paying"). */
export function PaymentSchedulesSection({ venueId }: { venueId: string }) {
  const query = useSalesSchedules(venueId)
  if (query.isPending) return <DetailSkeleton />
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  return <Editor key={query.dataUpdatedAt} data={query.data} venueId={venueId} />
}
